import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { unmaskMoney, unmaskNumber } from '../shared/utils/masks';
import { useSimuladorEnergiaSolarState } from './state';
import { usePostEntry } from '../api/use-post-entry';
import { getErrorData } from '../shared/utils/error';
import { arePostalCodesEqual, validatePostalCode } from '../shared/utils/validation';
import { SIMULATION_STEPS } from './simulation-steps';
import { useFindCEP } from '../api/use-find-cep';
import { devLog } from '../../../utils/browser';
import { useValidateAddress } from '../api/use-validate-address';
import { useScroll } from '../../../utils/useLayout';
import debounce from 'lodash/debounce';
import { useInitialDataSchema } from './initial-data/schema';
import { useInitialDataFields } from './initial-data/fields';
import { useSimulationActions } from './actions/use-simulation-actions';
import { MoneyTextFieldEmptyValue } from '../widgets/TextField/TextField';
import { useStoreActions } from './actions/use-store-actions';

const InitialValues = {
  projectValue: MoneyTextFieldEmptyValue,
  entryValue: MoneyTextFieldEmptyValue,
  averageUsage: MoneyTextFieldEmptyValue,
  store: '',
  searchStore: '',
  roofType: '',
  postalCode: '',
  address: '',
  neighborhood: '',
  city: '',
  state: '',
  noAddressNumber: false,
  addressNumber: '',
  storeCount: 0,
};

export function useFormInitialData() {
  const {
    cepData,
    setCepData,
    token,
    showErrorStep,
    hasSelectedType,
    simulationType,
    setResidencyType,
    simulationStep,
    getStoreData,
    setSimulationStep,
    setFormBasic,
    setSelectedStore,
  } = useSimuladorEnergiaSolarState();

  const { dynamicScrollTo } = useScroll();

  const handleError = (error, scroll = '.form-render', ctx = null) => {
    showErrorStep({
      ...getErrorData(error),
      overridePreviousStep: SIMULATION_STEPS.FORM_BASIC_INFO,
      scrollTo: scroll,
      ctx,
    });
  };

  const apiRegister = usePostEntry({ step: 'advance', token: token });
  const apiChooseStore = usePostEntry({ step: 'choose-store', token: token });
  const apiValidateAddress = useValidateAddress({ token });
  const apiFindCep = useFindCEP({ token });
  const { findStoresByCep, loadingStores, findCepGeocode } = useStoreActions();

  const [addressCompleted, setAddressCompleted] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [searchStoreResults, setSearchStoreResults] = useState([]);

  const isUsageStep = simulationStep?.id === SIMULATION_STEPS.FORM_USAGE.id;
  const isInitialStep = simulationStep?.id === SIMULATION_STEPS.FORM_BASIC_INFO.id;
  const enabled = isInitialStep && hasSelectedType;

  const simulation = useSimulationActions({
    step: isUsageStep ? SIMULATION_STEPS.FORM_USAGE : SIMULATION_STEPS.FORM_BASIC_INFO,
  });

  const validation = useInitialDataSchema({ showAddressFields, addressCompleted });

  const form = useForm({
    values: InitialValues,
    resolver: validation,
    criteriaMode: 'all',
    shouldFocusError: true,
    disabled: false,
  });

  const [
    currentRoofType,
    currentPostalCode,
    currentStore,
    isNoAddressNumberChecked,
    currentSearchStore,
    currentAverageUsage,
  ] = form.watch([
    'roofType',
    'postalCode',
    'store',
    'noAddressNumber',
    'searchStore',
    'averageUsage',
  ]);

  const handleCepChange = useCallback(
    async (newV = '', validate = false) => {
      const syncForm = (values = {}) => {
        form.setValue('address', values.address || '', validate);
        form.setValue('neighborhood', values.neighborhood || '', validate);
        form.setValue('city', values.city || '', validate);
        form.setValue('state', values.state || '', validate);
      };

      if (!validatePostalCode(newV)) {
        devLog('CEP inválido...');
        setCepData(null);
        setShowAddressFields(false);
        setAddressCompleted(false);
        return;
      }

      if (arePostalCodesEqual(newV, cepData?.andress?.zipCode)) {
        devLog('CEP já consultado, utilizando dados do estado');
        setShowAddressFields(true);
        syncForm(cepData.andress);
        return;
      }

      devLog('Consultando CEP...', newV);
      try {
        const result = await apiFindCep.mutateAsync(newV);
        devLog('Dados do CEP encontrados:', result);
        setCepData(result);
        setShowAddressFields(true);
        syncForm(result.andress);
      } catch (error) {
        devLog('Erro ao consultar CEP:', error);
        handleError(error, '.form-render', 'api-find-cep');
        setCepData(null);
        setShowAddressFields(false);
        syncForm({});
        setAddressCompleted(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cepData?.andress?.zipCode],
  );

  const setFieldValue = useCallback((field, value) => {
    form.setValue(field, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = useInitialDataFields({
    addressCompleted,
    handleCepChange,
    isNoAddressNumberChecked,
    currentRoofType,
    currentSearchStore,
    setFieldValue,
    showAddressFields,
  });

  const handleSearchStore = useCallback(
    async (tx = '') => {
      if (loadingStores) {
        devLog('[search-store] Loading in progress, skipping search');
        return;
      }

      form.setValue('store', '', false);
      form.clearErrors(['store', 'searchStore']);

      if (!tx) {
        devLog('[search-store] No search');
        setSearchStoreResults([]);
        return;
      }

      const cep = unmaskNumber(tx);
      if (!validatePostalCode(cep)) {
        devLog('[search-store] Invalid CEP length');
        setSearchStoreResults([]);
        return;
      }

      const geocode = await findCepGeocode(cep);
      devLog('[search-store] Geocode found:', geocode);

      try {
        const data = await findStoresByCep({
          latitude: geocode?.latitude,
          longitude: geocode?.longitude,
          postalCode: cep.replace(/\D/g, ''),
          city: geocode?.city,
          state: geocode?.state,
        });

        devLog('[search-store] Stores found:', data);

        form.setValue('storeCount', data?.length || 0, false);

        if (!data || data.length === 0) {
          setSearchStoreResults([]);
          form.setError('searchStore', {
            type: 'manual',
            message: 'Nenhuma loja encontrada para o CEP informado',
          });
          return;
        }

        form.clearErrors(['store', 'searchStore']);
        setSearchStoreResults(data);
      } catch (error) {
        handleError(error, '.form-render', 'api-find-stores');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findCepGeocode, findStoresByCep, loadingStores],
  );

  const delayedHandleSearchStore = useMemo(
    () => debounce(handleSearchStore, 500),
    [handleSearchStore],
  );

  const handleBackButton = () => {
    setAddressCompleted(false);
    form.setValue('storeCount', 0, false);

    if (isUsageStep) {
      setSimulationStep(SIMULATION_STEPS.FORM_BASIC_INFO);
      dynamicScrollTo('.form-render');
      return;
    }

    if (simulationType === 'new') {
      if (addressCompleted) {
        dynamicScrollTo('.text-rooftype');
        return;
      }
    }

    setResidencyType(null);
    dynamicScrollTo('top');
  };

  /** @type {import('react-hook-form').SubmitHandler<typeof InitialValues>} */
  const handleSubmit = async (values) => {
    if (simulationType === 'new' && !addressCompleted && apiFindCep.error) {
      await handleCepChange(values.postalCode, true);
      return;
    }

    if (simulationType === 'new' && !addressCompleted) {
      try {
        const body = {
          postalCode: values.postalCode,
          address: values.address,
          addressNumber: values.addressNumber,
          complement: values.complement,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          cityId: `${cepData?.andress?.cityId || '0'}`,
          addressType: `${cepData?.andress?.addressType || '1'}`,
        };

        setFormBasic(body);
        await apiValidateAddress.mutateAsync(body);
        setAddressCompleted(true);
        form.setValue('searchStore', unmaskNumber(values.postalCode), false);
        dynamicScrollTo('.form-render');
      } catch (error) {
        setAddressCompleted(false);
        handleError(error, '.form-render', 'api-validate-address');
      }

      return;
    }

    if (simulationType === 'new' && !isUsageStep) {
      setSimulationStep(SIMULATION_STEPS.FORM_USAGE);
      dynamicScrollTo('top');
      return;
    }

    try {
      const selectedStoreData = getStoreData(values.store);
      setSelectedStore(selectedStoreData);

      await apiRegister.mutateAsync({
        hasPreviousBudget: simulationType === 'existing',
        entryValue: unmaskMoney(values.entryValue),
        financedValue: unmaskMoney(values.projectValue),
        storeId: selectedStoreData?.id,
        roofType: values.roofType,
        averageUsage: isUsageStep ? unmaskMoney(values.averageUsage) : 0,
      });

      await apiChooseStore.mutateAsync({ storeId: `${values.store}` });

      await simulation.createSimulation({});

      if (simulationType === 'existing') {
        setCepData(null);
        setFormBasic(null);
      }

      dynamicScrollTo('top');
    } catch (error) {
      handleError(error, '.form-render', 'api-register');
      return;
    }
  };

  useEffect(() => {
    if (
      !enabled ||
      apiFindCep.isPending ||
      !validatePostalCode(currentPostalCode) ||
      addressCompleted ||
      simulationType === 'existing'
    ) {
      return;
    }

    if (apiFindCep.error) {
      devLog('Error in API Find CEP, skipping handleCepChange', apiFindCep.error);
      return;
    }

    devLog('Postal code updated, handling change...', currentPostalCode);
    if (arePostalCodesEqual(currentPostalCode, cepData?.andress?.zipCode)) {
      devLog('Postal code is the same as current CEP data, skipping API call');
      return;
    }

    handleCepChange(currentPostalCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPostalCode,
    enabled,
    apiFindCep.isPending,
    apiFindCep.error,
    addressCompleted,
    simulationType,
    cepData?.andress?.zipCode,
  ]);

  useEffect(() => {
    if (!enabled || isUsageStep || simulationType === 'existing') return;

    if (!addressCompleted) {
      form.setValue('store', '', false);
      form.clearErrors(['store', 'searchStore']);
      setSearchStoreResults([]);
      return;
    }

    delayedHandleSearchStore(currentSearchStore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchStore, enabled, addressCompleted, simulationType]);

  return {
    form,
    fields,
    loading: apiRegister.isPending || apiFindCep.isPending || apiValidateAddress.isPending,
    handleSubmit: form.handleSubmit(handleSubmit),
    handleBackButton,
    //
    isNoAddressNumberChecked,
    addressCompleted,
    setAddressCompleted,
    //
    currentStore,
    searchStoreResults,
    currentAverageUsage,
    loadingStores,
  };
}
